import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getImages } from "../../Api";
import { useSettings } from "../../context/SettingsContext";
import { useLoading } from "../../context/LoadingContext";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import { useLanguage } from "../../context/LanguageContext";
import { FaChevronRight } from "react-icons/fa6";

function PickingImage() {
  const { state } = useLocation();
  const shouldRefetch = state?.shouldRefetch ?? true; // Check if we should refetch images
  const [images, setImages] = useState([]);
  const { imageCount } = useSettings();
  const { loading, setLoading } = useLoading();
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [dotCount, setDotCount] = useState(1);
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { bgStyle, className } = useRoleBackground("pink");
  const { t } = useLanguage();

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const dotString = ".".repeat(dotCount);

  useEffect(() => {
    if (hasFetched.current || !shouldRefetch) {
      if (state?.allImages?.length === imageCount) {
        setImages(state.allImages.slice(0, imageCount));
        setLoading(false);
      }
      // Don’t set loading to false prematurely
      return;
    }

    hasFetched.current = true;
    fetchImages();
  }, [shouldRefetch]);

  const fetchImages = async () => {
    console.log("fetching");
    try {
      setLoading(true);
      setSelectedImageIndex(null); // Reset selection when getting new images
      const data = await getImages();

      if (data.success) {
        setImages(data.result.slice(0, imageCount));
        console.log("Fetched images and saving them:", data.result);
      }
    } catch (err) {
      console.error("Failed to fetch images:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleContinue = () => {
    if (selectedImageIndex !== null) {
      // navigate("/describeimagenormal"
      navigate("/writesentencenormal", {
        state: {
          selectedImage: {
            ...images[selectedImageIndex],
            index: selectedImageIndex,
          },
          allImages: images, // Pass entire set for later reference
          images: images, // Pass entire set for later reference
        },
      });
    }
  };

  // possibly add a reset images button
  const handleGetNewImages = () => {
    hasFetched.current = false; // Reset the flag
    fetchImages();
  };

  if (loading) {
    return (
      <div
        className={`flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl">
          {t('describer.loading', 'Loading images')}
          <span className="inline-block w-12 text-left">{dotString}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-[64px] flex flex-col items-center ${className}`}
      style={bgStyle}
    >
      <div className="w-[1052px] bg-white rounded-lg shadow-lg text-center">
        <h2
          className={`bg-pink400 w-full rounded-t-lg font-bold text-white text-xl font-[22px] py-[16px] px-[12px]`}
        >
          {t('describer.selectImage', 'SELECT AN IMAGE TO DESCRIBE:')}
        </h2>

        <div
          className={`grid pt-[34px] pb-[14px] px-[56px] gap-[12px] items-center ${
            imageCount === 6 ? "grid-cols-3 max-w-5xl" : "grid-cols-4"
          }`}
        >
          {images.map((image, index) => (
            <img
              key={index}
              src={image.url}
              alt={`Image ${index + 1}`}
              className={`size-[225px] rounded-[16px] cursor-pointer transition-all duration-200 ${
                selectedImageIndex === index
                  ? "border-[6px] border-aicom_pink"
                  : "hover:shadow-lg"
              }`}
              onClick={() => handleImageClick(index)}
            />
          ))}
        </div>

        <div className="flex gap-[24px] mb-[24px] ml-[884px]">
          <button
            className={`btn-pink-sm rounded-[10px] relative`}
            onClick={handleContinue}
            disabled={selectedImageIndex === null}
          >
            <span className="btn-text-pos-right-sm">{t('button.next', 'Next')} </span>
            <FaChevronRight className="arrow-pos-right-sm" />
          </button>
        </div>
        {/* {Get new images button} */}
        {/* <div className="mt-4">
          <button
            onClick={handleGetNewImages}
            className="text-sm px-4 py-2 bg-aicom_pink text-white rounded-md shadow hover:bg-pink-500 transition duration-200"
          >
            Get New Images
          </button>
        </div> */}
      </div>
    </div>
  );
}

export default PickingImage;
